import { useNavigate } from "react-router-dom";
import { useUserRoles } from "@/hooks/useUserRoles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Home, Building2, ClipboardList, UserCircle } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

const roleConfig: Record<AppRole, { label: string; icon: React.ElementType; path: string }> = {
  admin: { label: "Admin", icon: Shield, path: "/admin" },
  landlord: { label: "Landlord", icon: Building2, path: "/landlord-dashboard" },
  adjuster: { label: "Adjuster", icon: ClipboardList, path: "/adjuster-dashboard" },
  client: { label: "Client", icon: UserCircle, path: "/client-dashboard" },
  user: { label: "User", icon: Home, path: "/dashboard" },
};

interface RoleSwitcherProps {
  currentRole?: AppRole;
}

export function RoleSwitcher({ currentRole = "admin" }: RoleSwitcherProps) {
  const navigate = useNavigate();
  const { roles, loading } = useUserRoles();

  if (loading || roles.length <= 1) return null;

  const handleSwitch = (role: AppRole) => {
    const config = roleConfig[role];
    if (config) {
      navigate(config.path);
    }
  };

  return (
    <Select value={currentRole} onValueChange={(v) => handleSwitch(v as AppRole)}>
      <SelectTrigger className="w-[160px] h-8 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => {
          const config = roleConfig[role];
          if (!config) return null;
          const Icon = config.icon;
          return (
            <SelectItem key={role} value={role}>
              <span className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5" />
                {config.label} View
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
